/**
 * The dashboard — five tiles and two charts, from one call.
 *
 * Visual Reference: `design/screens/dashboard.html`. PRD §3.17: the screen
 * "answers one question: *does anything need me right now?*", and §6.5 makes it
 * a start-of-day instrument whose reader "is seeing eight hours of accumulated
 * events at once" — so it loads once from `GET /v1/w/{wid}/dashboard` rather
 * than polling.
 *
 * FOUR DELIBERATE DEVIATIONS FROM THE MOCKUP, each with a reason:
 *
 *   1. **The Cost per qualified lead tile renders for an Owner or Admin only.**
 *      The mockup draws six tiles; M1 drew five under DECISION-016, because the
 *      figure needs `ai_calls` joined to qualified leads, and Story 23.6 is the
 *      M2 story that closes manifest B3. It is money, so the API sends it only to a
 *      grant the Usage & cost page admits — an Editor, Reviewer or Viewer has five
 *      tiles and no figure in the response. Its zero state is words, "No analyses
 *      yet", never `$0.00`: DECISION-016's own reason is that a placeholder figure
 *      "reads as broken rather than as not-yet-built".
 *
 *   2. **The band chart carries a fourth, LOCKED segment.** The mockup draws
 *      three tones. DECISION-061 names this story by number: an over-cap lead
 *      is "counted as submissions and rendered as a distinct LOCKED segment …
 *      counted, not banded", because a band split is that withheld band
 *      arrived at by arithmetic. `pages/Funnels.tsx` already draws the same
 *      segment in `--mdx-line`, and this follows it. It is invisible on a
 *      workspace under its cap, which is every workspace until one is not.
 *
 *   3. **The headline, the eyebrow date and the needs-attention feed are not
 *      here.** They are Stories 12.4 and 12.2. This page renders the tiles and
 *      the charts; the two stories after it compose the rest onto the same
 *      response.
 *
 *   4. **Two near-miss geometries**, both recorded rather than absorbed
 *      silently (DECISION-064's precedent, and Story 11.2's `190px`). The
 *      legend's label column is `--mdx-space-72` where the export draws 88px,
 *      and its two numeric columns are `--mdx-space-40` where the export draws
 *      44px — a value `design/token-census.md` already FLAGS as untokenised on
 *      four screens and defers to the `/design` round-trip. The alternative was
 *      inventing two scale steps on one screen.
 *
 * THE FALLING DELTA IS NOT RED. `--mdx-hot` for a rise and `--mdx-slate` for a
 * fall, which is what the export draws on "▼ 2 pts" and "▼ 3" — RULE-002 keeps
 * red for things that have gone wrong, and fewer leads on a Tuesday has not.
 *
 * Stories 12.1 and 23.6.
 */
import { useCallback, useEffect, useState } from 'react'
import { apiGet } from '../lib/api'
import { formatMicros } from '../lib/money'
import { relative } from '../lib/relative'
import { setAttentionCount } from '../shell/attention'
import { useShell } from '../shell/workspace'
import { Skeleton } from '../ui/Skeleton'

/* ── the shape the API returns ──────────────────────────────────────────── */

type Delta = { delta: number; direction: 'up' | 'down' | 'flat'; label: string }

type Tile = {
  key: string
  label: string
  value: number
  suffix: string | null
  delta: Delta | null
  /** Story 23.6: `value` is integer micros — money, formatted by `lib/money.ts`. */
  unit?: 'micros'
  /** Story 23.6: a fall is good news — the cost tile's `▼ $0.06` is drawn in `--mdx-hot`. */
  lowerIsBetter?: boolean
  /** Story 23.6: no figure was computed, and why. */
  empty?: 'no_analyses' | 'no_qualified'
}

type BandWeek = {
  week: number
  hot: number
  review: number
  nurture: number
  locked: number
  total: number
}

type LegendRow = {
  tone: 'hot' | 'review' | 'nurture'
  label: string
  blurb: string
  leads: number
  share: number
  delta: Delta
}

type SubmissionWeek = { week: number; sent: number; finished: number; completion: number }

type AttentionItem = {
  id: string
  group: 'delivery' | 'decision' | 'volume'
  tone: 'error' | 'review' | 'nurture' | 'brand'
  glyph: string
  message: string
  at: number
  action: string
  href: string
}

type DashboardData = {
  tiles: Tile[]
  bands: { weeks: BandWeek[]; legend: LegendRow[]; total: number; delta: Delta }
  submissions: {
    weeks: SubmissionWeek[]
    sent: number
    finished: number
    completion: number
    todayCompletion: number | null
  }
  usage: { submissions: number; limit: number; period: string }
  attention: { items: AttentionItem[]; count: number }
  /** False when the metrics failed and the feed did not — Story 12.4. */
  metricsAvailable: boolean
  /** Nothing has ever arrived in the window. */
  empty: boolean
  headline: { sentence: string; reason: string } | null
}

/* ── formatting ─────────────────────────────────────────────────────────── */

const n = (value: number) => value.toLocaleString()

/** "21 Jul" — the axis label and the table's first column. */
const weekLabel = (at: number) =>
  new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })

/** "21 Jul – 24 Aug", the range the band card puts beside its title. */
function weekRange(weeks: BandWeek[]): string {
  const first = weeks[0]
  const last = weeks.at(-1)
  if (!first || !last) return ''
  return `${weekLabel(first.week)} – ${weekLabel(last.week + 6 * 86_400_000)}`
}

const ARROW = { up: '▲', down: '▼', flat: '·' } as const

/** `-1` is unlimited — migration 0002's own sentinel. */
const UNLIMITED = -1

/**
 * How much of the plot the TALLEST column fills.
 *
 * Read off the export rather than chosen: its tallest band column is 164px of
 * a 196px plot (0.837) and its tallest completion bar is 160px (0.816). The
 * rest is the room the count label above each bar needs — the labels ride on
 * the bars there, at different heights, rather than sitting in a row of their
 * own. A proportion, not a pixel, which is the same reason `Skeleton` takes its
 * width as a percentage: there is nothing here to put in `design/tokens.css`.
 */
const PLOT_FILL = 0.83

/**
 * The tone a movement is drawn in.
 *
 * `flat` reads slate because nothing moved, and a `·` in green would announce
 * an improvement that did not happen.
 */
const deltaTone = (direction: Delta['direction']) =>
  direction === 'up' ? 'text-mdx-hot' : 'text-mdx-slate'

function DeltaLine({
  delta,
  className = '',
  lowerIsBetter = false,
}: {
  delta: Delta
  className?: string
  /** A cost falls in the good colour and rises in slate — the rate tiles' rule, turned over. */
  lowerIsBetter?: boolean
}) {
  const good = lowerIsBetter
    ? delta.direction === 'down'
      ? 'up'
      : delta.direction === 'up'
        ? 'down'
        : 'flat'
    : delta.direction
  return (
    <span className={`${deltaTone(good)} ${className}`}>
      {ARROW[delta.direction]} {delta.label}
    </span>
  )
}

/* ── the shell of a card ────────────────────────────────────────────────── */

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <section
      className={`flex min-w-0 flex-col gap-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card ${className}`}
    >
      {children}
    </section>
  )
}

function CardHead({ title, meta }: { title: string; meta: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-mdx-12">
      <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{title}</span>
      <span className="text-mdx-13 text-mdx-slate tabular-nums">{meta}</span>
    </div>
  )
}

/* ── the tiles ──────────────────────────────────────────────────────────── */

/**
 * What the cost tile says when it has no figure (Story 23.6). "No analyses yet" is
 * the usage page's own zero-state phrase; with analyses and nothing qualified there
 * is spend and no lead to divide it by, and the tile says that instead of a dash.
 */
const EMPTY_VALUE: Record<NonNullable<Tile['empty']>, string> = {
  no_analyses: 'No analyses yet',
  no_qualified: 'No qualified leads yet',
}

function KpiTile({ tile, usage }: { tile: Tile; usage: DashboardData['usage'] }) {
  const meter = tile.key === 'plan_usage'
  const capped = meter && usage.limit !== UNLIMITED && usage.limit > 0
  const filled = capped ? Math.min(100, Math.round((usage.submissions / usage.limit) * 100)) : 0

  return (
    <div className="flex flex-col gap-mdx-6 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-20 shadow-mdx-card">
      <span className="text-mdx-13 font-mdx-semibold tracking-mdx-eyebrow text-mdx-slate uppercase">
        {tile.label}
      </span>
      {tile.empty ? (
        /* Words where a figure would be, at a size a sentence can sit in — the grid
         * row keeps this tile as tall as its neighbours either way. */
        <span className="text-mdx-20 font-mdx-semibold leading-mdx-heading text-mdx-ink">{EMPTY_VALUE[tile.empty]}</span>
      ) : (
        <span className="text-mdx-28 font-mdx-bold leading-mdx-tight tabular-nums">
          {tile.unit === 'micros' ? formatMicros(tile.value) : n(tile.value)}
          {tile.suffix ?? ''}
        </span>
      )}
      {tile.delta ? (
        <DeltaLine delta={tile.delta} lowerIsBetter={tile.lowerIsBetter} className="text-mdx-13 font-mdx-semibold" />
      ) : meter ? (
        <span className="text-mdx-13 font-mdx-semibold text-mdx-slate tabular-nums">
          {capped ? `of ${n(usage.limit)} submissions` : 'submissions this month'}
        </span>
      ) : (
        /* No comparison to draw — a first week, or a rate with no history.
         * The slot is kept so the tiles stay the same height. */
        <span className="text-mdx-13 font-mdx-semibold text-mdx-slate">No change yet</span>
      )}
      {capped ? (
        <span className="mt-mdx-2 block h-mdx-6 overflow-hidden rounded-mdx-pill bg-mdx-line-soft">
          <span
            className="block h-mdx-6 rounded-mdx-pill bg-mdx-brand"
            style={{ width: `${filled}%` }}
          />
        </span>
      ) : null}
    </div>
  )
}

/* ── the band chart ─────────────────────────────────────────────────────── */

/**
 * One stacked column.
 *
 * Heights are PERCENTAGES of the plot, never pixels — the same rule `Skeleton`
 * states for its width: "a proportion of a grid column is not a design token".
 * The segments are drawn nurture, review, hot from the top down, which puts the
 * qualified band on the baseline where the export has it.
 */
function BandColumn({ week, max }: { week: BandWeek; max: number }) {
  const stack = max > 0 ? (week.total / max) * 100 * PLOT_FILL : 0
  const part = (value: number) => (week.total > 0 ? (value / week.total) * 100 : 0)

  const segments = [
    { key: 'nurture', value: week.nurture, className: 'bg-mdx-nurture rounded-t-mdx-sm' },
    { key: 'review', value: week.review, className: 'bg-mdx-review' },
    { key: 'hot', value: week.hot, className: 'bg-mdx-hot' },
    /* DECISION-061's locked segment. Last, so it sits on the baseline under the
     * bands it is withholding, and in `--mdx-line` because it has no tone. */
    { key: 'locked', value: week.locked, className: 'bg-mdx-line' },
  ].filter((s) => s.value > 0)

  return (
    <div className="flex min-w-0 flex-1 flex-col justify-end gap-mdx-6">
      <span className="text-center text-mdx-13 font-mdx-bold tabular-nums">{week.total}</span>
      <span className="flex flex-col gap-mdx-2" style={{ height: `${stack}%` }}>
        {segments.map((segment, i) => (
          <span
            key={segment.key}
            className={`block ${segment.className} ${i === 0 ? 'rounded-t-mdx-sm' : ''}`}
            style={{ height: `${part(segment.value)}%` }}
          />
        ))}
      </span>
    </div>
  )
}

const SWATCH: Record<LegendRow['tone'], string> = {
  hot: 'bg-mdx-hot',
  review: 'bg-mdx-review',
  nurture: 'bg-mdx-nurture',
}

function LegendRowView({ row }: { row: LegendRow }) {
  return (
    <div className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-8 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12 text-mdx-15">
      <span
        className={`size-mdx-evidence-icon shrink-0 rounded-mdx-control-sm ${SWATCH[row.tone]}`}
      />
      <span className="w-mdx-72 font-mdx-semibold">{row.label}</span>
      <span className="min-w-0 flex-1 basis-mdx-row-identity-sm text-mdx-slate">{row.blurb}</span>
      <span className="font-mdx-bold whitespace-nowrap tabular-nums">{n(row.leads)}</span>
      <span className="w-mdx-40 text-right text-mdx-14 whitespace-nowrap text-mdx-slate tabular-nums">
        {row.share}%
      </span>
      <DeltaLine
        delta={row.delta}
        className="w-mdx-40 text-right text-mdx-14 font-mdx-semibold whitespace-nowrap"
      />
    </div>
  )
}

function BandChart({ bands }: { bands: DashboardData['bands'] }) {
  const max = Math.max(1, ...bands.weeks.map((w) => w.total))
  const label = bands.weeks
    .map((w) => `${weekLabel(w.week)}: ${w.total}`)
    .join(', ')

  return (
    <Card>
      <CardHead title="Leads by band, 5 weeks" meta={weekRange(bands.weeks)} />
      <div className="flex flex-col gap-mdx-14">
        <div className="flex items-baseline gap-mdx-10">
          <span className="text-mdx-28 font-mdx-bold leading-mdx-tight tabular-nums">
            {n(bands.total)}
          </span>
          <span className="text-mdx-15 text-mdx-slate">
            leads scored ·{' '}
            <DeltaLine delta={bands.delta} className="font-mdx-semibold" /> on the 5 weeks before
          </span>
        </div>

        <div
          role="img"
          aria-label={`Stacked columns of leads per week by band. ${label}.`}
          className="flex flex-col gap-mdx-8"
        >
          <div className="mdx-plot flex items-stretch gap-mdx-10 px-mdx-2">
            {bands.weeks.map((week) => (
              <BandColumn key={week.week} week={week} max={max} />
            ))}
          </div>
          <div className="flex gap-mdx-10 px-mdx-2">
            {bands.weeks.map((week) => (
              <span
                key={week.week}
                className="min-w-0 flex-1 text-center text-mdx-13 text-mdx-slate"
              >
                {weekLabel(week.week)}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-mdx-8">
          {bands.legend.map((row) => (
            <LegendRowView key={row.tone} row={row} />
          ))}
        </div>

        {/* Rendered verbatim — the story quotes this line as a business rule. */}
        <p className="m-0 text-mdx-13 text-mdx-slate">
          Bands are fixed. The ladder moved up because scores did, not because the thresholds
          changed.
        </p>
      </div>
    </Card>
  )
}

/* ── the completion chart ───────────────────────────────────────────────── */

function CompletionChart({ submissions }: { submissions: DashboardData['submissions'] }) {
  const weeks = submissions.weeks
  const max = Math.max(1, ...weeks.map((w) => w.sent))
  const currentWeek = weeks.at(-1)?.week

  return (
    <Card>
      <CardHead
        title="Submissions and completion rate"
        meta={
          submissions.todayCompletion === null
            ? 'nothing today yet'
            : `${submissions.todayCompletion}% today`
        }
      />
      <div className="flex flex-col gap-mdx-14">
        <div className="flex items-baseline gap-mdx-10">
          <span className="text-mdx-28 font-mdx-bold leading-mdx-tight tabular-nums">
            {n(submissions.sent)}
          </span>
          <span className="text-mdx-15 text-mdx-slate">
            submissions in 5 weeks · {n(submissions.finished)} finished the form,{' '}
            {submissions.completion}% overall
          </span>
        </div>

        <div
          role="img"
          aria-label={`Bars of weekly submissions. ${weeks
            .map((w) => `${weekLabel(w.week)}: ${w.sent} sent, ${w.completion}% finished`)
            .join('; ')}.`}
          className="flex flex-col gap-mdx-8"
        >
          <div className="mdx-plot flex items-stretch gap-mdx-10 px-mdx-2">
            {weeks.map((week) => {
              const current = week.week === currentWeek
              return (
                <div key={week.week} className="flex min-w-0 flex-1 flex-col justify-end gap-mdx-6">
                  <span
                    className={`text-center text-mdx-13 font-mdx-bold tabular-nums ${
                      current ? 'text-mdx-brand-deep' : 'text-mdx-slate'
                    }`}
                  >
                    {week.completion}%
                  </span>
                  <span
                    className={`block rounded-t-mdx-sm ${
                      current ? 'bg-mdx-brand' : 'bg-mdx-brand-tint'
                    }`}
                    style={{ height: `${(week.sent / max) * 100 * PLOT_FILL}%` }}
                  />
                </div>
              )
            })}
          </div>
          <div className="flex gap-mdx-10 px-mdx-2">
            {weeks.map((week) => (
              <span
                key={week.week}
                className="min-w-0 flex-1 text-center text-mdx-13 text-mdx-slate"
              >
                {weekLabel(week.week)}
              </span>
            ))}
          </div>
          <span className="text-mdx-13 text-mdx-slate">
            Bar height is submissions sent · figure above each bar is the share that finished. This
            week is still running.
          </span>
        </div>

        <div className="flex flex-col gap-mdx-2">
          <div className="flex items-center gap-mdx-12 rounded-mdx-control px-mdx-16 py-mdx-10 text-mdx-12 font-mdx-bold tracking-mdx-eyebrow text-mdx-slate uppercase">
            <span className="min-w-0 flex-1">Week of</span>
            <span className="w-mdx-56 text-right">Sent</span>
            <span className="w-mdx-56 text-right">Done</span>
            {/* "Completion" needs about 95px and has 40 once the bar is gone,
                so at 375 it takes the card's own other word rather than
                wrapping "Week of" onto two lines to make room for itself. */}
            <span className="w-mdx-40 text-right sm:w-mdx-field-badge">
              <span className="sm:hidden">Rate</span>
              <span className="hidden sm:inline">Completion</span>
            </span>
          </div>
          {weeks.map((week) => {
            const current = week.week === currentWeek
            return (
              <div
                key={week.week}
                className={`flex items-center gap-mdx-12 rounded-mdx-control px-mdx-16 py-mdx-12 text-mdx-15 ${
                  current ? 'bg-mdx-brand-tint' : ''
                }`}
              >
                <span
                  className={`min-w-0 flex-1 whitespace-nowrap ${
                    current ? 'font-mdx-semibold text-mdx-brand-deep' : 'text-mdx-slate'
                  }`}
                >
                  {weekLabel(week.week)}
                  {current ? ' · now' : ''}
                </span>
                <span
                  className={`w-mdx-56 text-right tabular-nums ${current ? 'font-mdx-bold' : ''}`}
                >
                  {n(week.sent)}
                </span>
                <span
                  className={`w-mdx-56 text-right tabular-nums ${
                    current ? 'font-mdx-semibold' : 'text-mdx-slate'
                  }`}
                >
                  {n(week.finished)}
                </span>
                <span className="flex w-mdx-40 items-center justify-end gap-mdx-8 sm:w-mdx-field-badge">
                  {/* THE BAR IS A SECOND ENCODING of the number beside it, and
                      at 375px the four columns and the week's own date cannot
                      all fit — the export crushes the date to about 19px rather
                      than choosing. The redundant half is the one that goes. */}
                  <span
                    className={`hidden h-mdx-8 flex-1 overflow-hidden rounded-mdx-pill sm:block ${
                      current ? 'bg-mdx-white' : 'bg-mdx-line-soft'
                    }`}
                  >
                    <span
                      className={`block h-mdx-8 ${current ? 'bg-mdx-brand' : 'bg-mdx-brand-tint'}`}
                      style={{ width: `${week.completion}%` }}
                    />
                  </span>
                  <span
                    className={`w-mdx-40 text-right text-mdx-14 tabular-nums ${
                      current ? 'font-mdx-bold text-mdx-brand-deep' : 'text-mdx-slate'
                    }`}
                  >
                    {week.completion}%
                  </span>
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </Card>
  )
}

/* ── the Verdict, and the states beneath it · Story 12.4 ────────────────── */

/**
 * The eyebrow — "Thursday 21 August · 8:04".
 *
 * THE BROWSER'S CLOCK, deliberately, and it is one of two places in the product
 * that is right to be: `DeliveryLog`'s own note says the same thing — "a person
 * reading a screen and '09:12' means the clock on their wall." The digest
 * scheduler uses the FUNNEL's zone because a send time is a promise about
 * somebody's morning; this is a person looking at a screen.
 *
 * Note the counts beneath it are UTC days (`services/dashboard`), so around
 * midnight the eyebrow and "today" can name different days. `docs/BACKLOG.md`
 * carries that with the rest of the timezone question DECISION-083 queued.
 */
function eyebrow(now: number): string {
  const date = new Date(now)
  return `${date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })} · ${date.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' })}`
}

/**
 * The Verdict block — PRD §1.9 and RULE-003: every screen leads with a sentence.
 *
 * The same three-part shape `auth/AuthPanel.tsx` uses for its own H1, because
 * it is the same element in the design system: eyebrow, verdict, reason.
 */
function Headline({
  sentence,
  reason,
  now,
}: {
  sentence: string
  reason: string
  now: number
}) {
  return (
    <div className="flex flex-col gap-mdx-8">
      <span className="text-mdx-12 font-mdx-bold tracking-mdx-eyebrow text-mdx-slate uppercase">
        {eyebrow(now)}
      </span>
      <h1 className="m-0 text-balance text-mdx-verdict font-mdx-bold leading-mdx-verdict tracking-mdx-verdict">
        {sentence}
      </h1>
      <p className="m-0 max-w-mdx-measure-52 text-mdx-18 leading-mdx-reason text-mdx-slate">
        {reason}
      </p>
    </div>
  )
}

/**
 * The empty state — one onboarding card where the tiles and charts were.
 *
 * "every empty state is an invitation to act" (ARCH §7.5, PRD §1.7). Every word
 * is the export's, including the button, and the button is the invitation: it
 * goes to `/funnels`, which is where a first funnel is made.
 */
function Onboarding() {
  return (
    <Card className="items-start gap-mdx-8 py-mdx-48">
      <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
        Your first lead will appear here
      </span>
      <p className="m-0 max-w-mdx-measure-44 text-mdx-15 text-mdx-slate">
        Build a funnel, put it on the page your buyers already visit, and Leadgible will tell you who
        to call.
      </p>
      <a
        href="/funnels"
        className="mt-mdx-4 rounded-mdx-control bg-mdx-brand px-mdx-24 py-mdx-12 text-mdx-15 font-mdx-semibold text-mdx-white no-underline transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-brand-deep"
      >
        Set up your first funnel
      </a>
    </Card>
  )
}

/**
 * The error state — the tiles are gone and the feed is not.
 *
 * RECOVERABLE, which is what the button is for: the metrics are a read, and a
 * read that failed once is worth asking for again. Every word is the export's,
 * and the middle sentence is the one that matters — "Your leads are safe and
 * the list below is current" is a promise the API keeps by composing the feed
 * from tables the metrics never touched.
 */
function MetricsError({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="items-start gap-mdx-8 py-mdx-48">
      <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
        Your numbers didn&rsquo;t load
      </span>
      <p className="m-0 max-w-mdx-measure-44 text-mdx-15 text-mdx-slate">
        The metrics service timed out. Your leads are safe and the list below is current — load the
        numbers again when you&rsquo;re ready.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-mdx-4 cursor-pointer rounded-mdx-control border-0 bg-mdx-brand px-mdx-24 py-mdx-12 font-mdx text-mdx-15 font-mdx-semibold text-mdx-white transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-brand-deep"
      >
        Try again
      </button>
    </Card>
  )
}

/* ── the needs-attention feed ───────────────────────────────────────────── */

/**
 * The three groups, in the export's order — which IS the urgency order the
 * contract asks for. Mirrored from `services/dashboard/attention.ts`'s
 * `ATTENTION_GROUPS`, and `tests/attention-routes.test.ts` fails if the two
 * lists ever stop matching: the API decides which group an item is in, and a
 * key it names that this cannot draw would silently drop the row.
 */
const GROUPS = [
  { key: 'delivery', label: 'Delivery' },
  { key: 'decision', label: 'Needs a decision' },
  { key: 'volume', label: 'Volume and plan' },
] as const

/** The export's four glyph squares. RULE-002 governs which colour means what. */
const GLYPH: Record<AttentionItem['tone'], string> = {
  error: 'bg-mdx-error',
  review: 'bg-mdx-review',
  nurture: 'bg-mdx-nurture',
  brand: 'bg-mdx-brand',
}

function AttentionRow({ item, now }: { item: AttentionItem; now: number }) {
  return (
    <a
      href={item.href}
      className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-8 rounded-mdx-control px-mdx-16 py-mdx-14 text-mdx-ink no-underline transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground"
    >
      <span
        aria-hidden="true"
        className={`flex size-mdx-evidence-icon shrink-0 items-center justify-center rounded-mdx-control-sm text-mdx-14 font-mdx-bold text-mdx-white ${GLYPH[item.tone]}`}
      >
        {item.glyph}
      </span>
      <span className="min-w-0 flex-1 basis-mdx-row-identity text-mdx-15 font-mdx-medium">
        {item.message}
      </span>
      <span className="whitespace-nowrap text-mdx-13 text-mdx-slate">
        {relative(item.at, now)}
      </span>
      <span className="whitespace-nowrap text-mdx-13 font-mdx-semibold text-mdx-brand-deep">
        {item.action}
      </span>
    </a>
  )
}

/**
 * The feed.
 *
 * THE EMPTY STATE IS AN ACHIEVEMENT, not a gap — the export's own note for its
 * empty capture says "the feed reads as an achievement", and its copy is
 * rendered verbatim: the meta reads `All clear` and the body reads
 * "Nothing needs you right now." at the same 17px the card title uses, because
 * it IS the answer to the question the screen asks.
 *
 * A VIEWER SEES THAT SENTENCE AND IT IS TRUE FOR THEM. Every source is gated
 * server-side by the contract row of the route its item resolves to, so a
 * Viewer — who may open none of them — gets an empty feed. The second sentence
 * says whose it is rather than leaving them to wonder what they are missing.
 */
function AttentionFeed({ feed, now }: { feed: DashboardData['attention']; now: number }) {
  const grouped = GROUPS.map((group) => ({
    ...group,
    items: feed.items.filter((item) => item.group === group.key),
  })).filter((group) => group.items.length > 0)

  return (
    <section
      aria-label="Needs your attention"
      className="flex flex-col gap-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-16 py-mdx-24 shadow-mdx-card"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-mdx-12 px-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">Needs your attention</span>
        <span className="text-mdx-13 text-mdx-slate">
          {feed.count === 0
            ? 'All clear'
            : `${feed.count} ${feed.count === 1 ? 'item' : 'items'} · newest first within each group`}
        </span>
      </div>

      {grouped.length === 0 ? (
        <div className="flex flex-col gap-mdx-8 px-mdx-8">
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
            Nothing needs you right now.
          </span>
          {/* The export's own sentence, verbatim. Story 12.2 wrote a different
              one about permissions; the mockup is the source of truth and
              `docs/BACKLOG.md` carries the permission-limited state as a
              question for the round-trip rather than as invented copy. */}
          <p className="m-0 max-w-mdx-measure-44 text-mdx-15 text-mdx-slate">
            Every queue is clear and every dispatch went through. We&rsquo;ll put anything that
            needs a decision here.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-mdx-20">
          {grouped.map((group) => (
            <div key={group.key} className="flex flex-col gap-mdx-8">
              <span className="px-mdx-8 text-mdx-12 font-mdx-bold tracking-mdx-eyebrow text-mdx-slate uppercase">
                {group.label}
              </span>
              {group.items.map((item) => (
                <AttentionRow key={item.id} item={item} now={now} />
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

/* ── loading ────────────────────────────────────────────────────────────── */

/**
 * The real layout with skeletons in the slots — `design/DESIGN-SYSTEM.md` §2,
 * "**Never a bare centred spinner on a full page; preserve the real layout so
 * nothing jumps when content lands.**" The plot keeps its own height, so the
 * charts do not grow when the data arrives.
 */
function Loading() {
  return (
    <div className="flex flex-col gap-mdx-28" data-testid="dashboard-loading">
      {/* The Verdict's own slot. Without it the whole page jumps down by the
          height of an H1 when the sentence lands, which is the reflow
          `design/DESIGN-SYSTEM.md` §2 asks the loading state to prevent. */}
      <div className="flex flex-col gap-mdx-8">
        <Skeleton className="h-mdx-12 rounded-mdx-sm" style={{ width: '22%' }} />
        <Skeleton className="h-mdx-40 rounded-mdx-control" style={{ width: '54%' }} />
        <Skeleton className="h-mdx-18 rounded-mdx-sm" style={{ width: '36%' }} />
      </div>
      <div className="mdx-tile-grid gap-mdx-16">
        {[92, 78, 64, 70, 86].map((width, i) => (
          <div
            key={i}
            className="flex flex-col gap-mdx-6 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-20 shadow-mdx-card"
          >
            <Skeleton className="h-mdx-14 rounded-mdx-sm" style={{ width: '52%' }} />
            <Skeleton className="h-mdx-28 rounded-mdx-control-sm" style={{ width: `${width}%` }} />
            <Skeleton className="h-mdx-14 rounded-mdx-sm" style={{ width: '40%' }} />
          </div>
        ))}
      </div>
      <div className="mdx-chart-grid gap-mdx-16">
        {[0, 1].map((i) => (
          <Card key={i}>
            <Skeleton className="h-mdx-18 rounded-mdx-sm" style={{ width: '46%' }} />
            <Skeleton className="mdx-plot rounded-mdx-sm" style={{ width: '100%' }} />
            <div className="flex flex-col gap-mdx-8">
              {[0, 1, 2].map((j) => (
                <Skeleton key={j} className="h-mdx-48 rounded-mdx-control" style={{ width: '100%' }} />
              ))}
            </div>
          </Card>
        ))}
      </div>
      {/* The feed keeps its own geometry too — it is the part of this screen
          worth reading first, and a card that appears late is a card the eye
          has already moved past. */}
      <Card className="px-mdx-16! py-mdx-24!">
        <Skeleton className="h-mdx-18 rounded-mdx-sm" style={{ width: '38%' }} />
        <div className="flex flex-col gap-mdx-8">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-mdx-48 rounded-mdx-control" style={{ width: '100%' }} />
          ))}
        </div>
      </Card>
    </div>
  )
}

/* ── the page ───────────────────────────────────────────────────────────── */

export function Dashboard() {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null

  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  const load = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    setFailed(false)
    try {
      const view = await apiGet<DashboardData>(`/v1/w/${workspaceId}/dashboard`)
      setData(view)
      // The shell's bell reads this. One call answers both — `shell/attention.ts`
      // says why it is not a second fetch.
      setAttentionCount(view.attention.count)
    } catch {
      // The WHOLE call failed — not the metrics half, which comes back as
      // `metricsAvailable: false` with the feed intact. There is nothing to
      // preserve here, so the page says so and offers the reload.
      setFailed(true)
      // Not zero. A count nobody could load is unknown, and a bell showing `0`
      // would say "nothing needs you" on the strength of a failed request.
      setAttentionCount(null)
    }
    setLoading(false)
  }, [workspaceId])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) return <Loading />

  if (failed || !data) {
    return (
      <Card>
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
          Today&rsquo;s numbers didn&rsquo;t load
        </span>
        <p className="m-0 max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
          Nothing is wrong with your funnels — this screen could not reach them. Reload the page to
          try again.
        </p>
      </Card>
    )
  }

  const now = Date.now()

  return (
    <div className="flex flex-col gap-mdx-28">
      {/*
        THE SCREEN LEADS WITH A SENTENCE IN ALL THREE STATES, which is the whole
        of RULE-003. When the metrics failed there is no headline to compose —
        it is arithmetic over the counts that did not arrive — so the error
        card's own title is the sentence that leads instead. Drawing the last
        one over today's failure would be the screen saying something it does
        not know.
      */}
      {data.headline ? (
        <Headline sentence={data.headline.sentence} reason={data.headline.reason} now={now} />
      ) : null}

      {!data.metricsAvailable ? (
        <MetricsError onRetry={() => void load()} />
      ) : data.empty ? (
        <Onboarding />
      ) : (
        <>
          <div className="mdx-tile-grid gap-mdx-16">
            {data.tiles.map((tile) => (
              <KpiTile key={tile.key} tile={tile} usage={data.usage} />
            ))}
          </div>
          <div className="mdx-chart-grid gap-mdx-16">
            <BandChart bands={data.bands} />
            <CompletionChart submissions={data.submissions} />
          </div>
        </>
      )}

      {/* PRESERVED IN EVERY STATE. The feed is the part worth keeping when the
          metrics fail, and on an empty workspace it is the achievement the
          export's note describes. */}
      <AttentionFeed feed={data.attention} now={now} />
    </div>
  )
}
